import { Button as ButtonV1, Input, Radio } from '@bbl-digital/snorre'
import { Button } from '@bbl-digital/snorre-v2'
import { useFormik } from 'formik'
import { FC } from 'react'
import { Column, Row } from '../../styles'
import * as Yup from 'yup'
import {
  BooleanValidation,
  EmailValidation,
  NoValidation,
  PhoneValidation,
} from 'shared/utils/validations'
import { IMessageRecieptRecipient } from 'shared/models/Message'

type ContactFormValues = {
  isCompany: boolean
  firstname: string
  lastname: string
  mobile: string
  email: string
  companyName: string
}

interface IProps {
  contact?: IMessageRecieptRecipient
  isSms?: boolean
  processing: boolean
  onSubmit: (values: ContactFormValues) => void
  onCancel: () => void
}

const contactValidationSchema = Yup.object().shape({
  isCompany: BooleanValidation,
  firstname: NoValidation,
  lastname: Yup.string().when('isCompany', {
    is: false,
    then: Yup.string().required('Etternavn er påkrevd for privatperson'),
  }),
  mobile: PhoneValidation.notRequired(),
  email: EmailValidation.notRequired(),
  companyName: Yup.string().when('isCompany', {
    is: true,
    then: Yup.string().required('Firmanavn er påkrevd for firma'),
  }),
})

const ContactForm: FC<IProps> = ({
  contact,
  isSms,
  processing,
  onSubmit,
  onCancel,
}) => {
  const {
    values,
    errors,
    touched,
    setFieldTouched,
    setFieldValue,
    submitForm,
  } = useFormik({
    initialValues: {
      isCompany: false,
      firstname: '',
      lastname: '',
      mobile: (isSms && contact?.phonenumber) || '',
      email: (!isSms && contact?.email) || '',
      companyName: '',
    },
    validationSchema: contactValidationSchema,
    onSubmit: onSubmit,
  })

  return (
    <Column gap="1em">
      <Row>
        <Radio
          name="type"
          checked={values.isCompany}
          onChange={() => setFieldValue('isCompany', true)}
        >
          Firma
        </Radio>
        <Radio
          name="type"
          checked={!values.isCompany}
          onChange={() => setFieldValue('isCompany', false)}
        >
          Privatperson
        </Radio>
      </Row>
      <Input
        label="For- og mellomnavn"
        maxlength={40}
        value={values.firstname}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFieldValue('firstname', e.target.value)
        }
        onBlur={() => setFieldTouched('firstname')}
        validation
        invalidMessage={(touched.firstname && errors.firstname) || undefined}
      />
      <Input
        label="Etternavn"
        maxlength={80}
        value={values.lastname}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFieldValue('lastname', e.target.value)
        }
        onBlur={() => setFieldTouched('lastname')}
        validation
        invalidMessage={(touched.lastname && errors.lastname) || undefined}
      />
      <Input
        label="E-post"
        maxlength={50}
        value={values.email}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFieldValue('email', e.target.value)
        }
        onBlur={() => setFieldTouched('email')}
        validation
        invalidMessage={(touched.email && errors.email) || undefined}
        disabled={!isSms}
      />

      <Input
        label="Mobil"
        value={values.mobile}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFieldValue('mobile', e.target.value.replace(/\s/g, ''))
        }
        onBlur={() => setFieldTouched('mobile')}
        validation
        invalidMessage={(touched.mobile && errors.mobile) || undefined}
        disabled={isSms}
      />

      <Input
        label="Firma"
        maxlength={80}
        value={values.companyName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFieldValue('companyName', e.target.value)
        }
        onBlur={() => setFieldTouched('companyName')}
        validation
        invalidMessage={
          (touched.companyName && errors.companyName) || undefined
        }
      />

      <Row justify="space-between">
        <Button onClick={onCancel} variant="secondary">
          Tilbake
        </Button>
        <ButtonV1 onClick={submitForm} loading={processing}>
          Legg til
        </ButtonV1>
      </Row>
    </Column>
  )
}

export default ContactForm
